import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import { store } from "../../../../../store";
import { getAnalysesByStudy } from "../../../../../redux/actions/analysis";
import {
  createSample,
  deleteSample,
  editSample,
  getSample,
} from "../../../../../redux/actions/sample";
import { getProductionsByCenter } from "../../../../../redux/actions/production";
import { getPatientsByCenter } from "../../../../../redux/actions/patient";
import {
  closeLoadingAlert,
  showErrorAlert,
  showLoadingAlert,
} from "../../../../ui/utils/AlertUtils";
import { getSampleSetupItems } from "../../../../../services/sampleSetupService";
import { renderAnalysisContainerLabel } from "../../../../ui/AnalysisContainerLabel";
import {
  compareAnalysesByColorAndName,
  normalizeAnalysisColor,
} from "../analysis/AnalysisColorPalette";

const OTHER_KIT_VALUE = "__other__";

const AnalysisName = ({ analysis }) => (
  <Box
    component="span"
    sx={{
      color: normalizeAnalysisColor(analysis?.color, "text.primary"),
      textShadow: "0 0 1px rgba(0, 0, 0, 0.6)",
      fontWeight: analysis?.color ? 600 : 400,
    }}>
    {analysis?.name || ""}
  </Box>
);

const renderAnalysisNames = (analysisOptions) =>
  analysisOptions.map((analysis, index) => (
    <React.Fragment key={analysis.id}>
      {index > 0 ? ", " : null}
      <AnalysisName analysis={analysis} />
    </React.Fragment>
  ));

const clearableSelectSx = {
  "& .MuiSelect-select": {
    paddingRight: "64px !important",
  },
};

const clearButtonSx = {
  position: "absolute",
  right: 32,
  top: "50%",
  zIndex: 1,
  transform: "translateY(-50%)",
};

const stopSelectFromOpening = (event) => {
  event.stopPropagation();
};

const normalizeProductionStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const getProductionLabel = (production) =>
  [
    production?.reference,
    production?.batchNumber,
    production?.Material?.name || production?.material?.name,
  ]
    .filter(Boolean)
    .join(" - ");

const normalizeSampleType = (type) => String(type || "").trim();

const normalizeSampleTypeForSubmit = (type) =>
  normalizeSampleType(type);

const normalizeKitValue = (productionId) =>
  productionId && productionId !== OTHER_KIT_VALUE ? productionId : "";

const getProductionMaterialId = (production = {}) =>
  production.materialId ??
  production.material_id ??
  production.Material?.id ??
  production.material?.id ??
  null;

const getProductionMaterialType = (production = {}) =>
  String(production.Material?.type || production.material?.type || "")
    .trim()
    .toLowerCase();

const getAnalysisMaterialIds = (analysis = {}) =>
  (analysis.materials || analysis.Materials || [])
    .map((material) => Number(material.id))
    .filter((materialId) => Number.isInteger(materialId));

const getAnalysisContainer = (analysis = {}) => {
  const nestedContainer = analysis.analysisContainer;

  if (nestedContainer) {
    return nestedContainer;
  }

  const id = analysis.analysisContainerId ?? null;
  const name = analysis.containerName ?? analysis.container_name ?? "";
  const type = analysis.containerType ?? analysis.container_type ?? null;
  const size = analysis.containerSize ?? analysis.container_size ?? null;
  const sizeUnit =
    analysis.containerSizeUnit ?? analysis.container_size_unit ?? null;
  const hasSize = size !== null && size !== undefined && size !== "";

  if (!id && !name && !type && !hasSize && !sizeUnit) {
    return null;
  }

  return { id, name, type, size, sizeUnit };
};

const getAnalysisContainerKey = (container = {}) =>
  container.id
    ? `id:${container.id}`
    : [
        container.name,
        container.type,
        container.size,
        container.sizeUnit ?? container.size_unit,
      ].join("|");

const renderAnalysisContainerList = (containers) => (
  <Box
    component="span"
    sx={{ display: "inline-flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
    {containers.map((container) => (
      <React.Fragment key={getAnalysisContainerKey(container)}>
        {renderAnalysisContainerLabel(container)}
      </React.Fragment>
    ))}
  </Box>
);

const getAnalysisVisits = (analysis = {}) =>
  analysis.visits || analysis.Visits || [];

const getAnalysisSampleTypes = (analysis = {}) => analysis.sampleTypes || [];

const getAnalysisQuantityRows = (analysis = {}) => {
  let quantity = analysis.quantity;

  if (typeof quantity === "string") {
    try {
      quantity = JSON.parse(quantity);
    } catch (_error) {
      return [];
    }
  }

  return Array.isArray(quantity) ? quantity : [];
};

const getAnalysisVisitQuantity = (analysis, visitId, sampleTypeId) => {
  if (!analysis) {
    return null;
  }

  const quantityRow = getAnalysisQuantityRows(analysis).find(
    (row) => Number(row?.visitId) === Number(visitId),
  );
  if (!quantityRow) {
    return null;
  }

  const normalizedSampleTypeId = Number(sampleTypeId);
  const rawQuantity =
    Number.isInteger(normalizedSampleTypeId) && normalizedSampleTypeId > 0
      ? quantityRow.sampleTypes?.[String(normalizedSampleTypeId)]
      : quantityRow.default;
  const quantity = Number(rawQuantity);

  return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null;
};

const getAnalysisVisitsTotalQuantity = (
  analysis,
  visitIds,
  sampleTypeId,
) => {
  if (!analysis || !visitIds.length) {
    return null;
  }

  const quantities = visitIds.map((visitId) =>
    getAnalysisVisitQuantity(analysis, visitId, sampleTypeId),
  );

  return quantities.some((quantity) => quantity === null)
    ? null
    : quantities.reduce((total, quantity) => total + quantity, 0);
};

const getRelatedVisitIds = (analyses, analysisIds) => {
  const selectedAnalysisIds = new Set(
    analysisIds.map((analysisId) => Number(analysisId)),
  );

  return new Set(
    analyses
      .filter((analysis) => selectedAnalysisIds.has(Number(analysis.id)))
      .flatMap(getAnalysisVisits)
      .map((visit) => Number(visit.id))
      .filter((visitId) => Number.isInteger(visitId) && visitId > 0),
  );
};

const getRelatedAnalysisIds = (analyses, visitIds) => {
  const selectedVisitIds = new Set(
    visitIds.map((visitId) => Number(visitId)),
  );

  return new Set(
    analyses
      .filter((analysis) =>
        getAnalysisVisits(analysis).some((visit) =>
          selectedVisitIds.has(Number(visit.id)),
        ),
      )
      .map((analysis) => Number(analysis.id))
      .filter(
        (analysisId) => Number.isInteger(analysisId) && analysisId > 0,
      ),
  );
};

const getVisitIdsAfterAnalysisChange = ({
  autoSelectSingleVisit,
  currentVisitIds,
  nextAnalysisIds,
  nextRelatedVisitIds,
  visitOptions,
  visitsDriveSelection,
}) => {
  if (visitsDriveSelection || nextAnalysisIds.length === 0) {
    return currentVisitIds;
  }

  const compatibleVisitIds = currentVisitIds.filter((visitId) =>
    nextRelatedVisitIds.has(Number(visitId)),
  );

  if (!autoSelectSingleVisit || compatibleVisitIds.length > 0) {
    return compatibleVisitIds;
  }

  const compatibleVisitOptions = visitOptions.filter((visit) =>
    nextRelatedVisitIds.has(Number(visit.id)),
  );

  return compatibleVisitOptions.length === 1
    ? [Number(compatibleVisitOptions[0].id)]
    : compatibleVisitIds;
};

const getPatientCode = (patient = {}) => {
  const code =
    patient.patientCode ?? patient.patient_code ?? patient.code ?? patient.number;

  return code === null || code === undefined ? "" : String(code);
};

const getPatientInclusionNumber = (patient = {}) => {
  const inclusionNumber = patient.inclusionNumber ?? patient.inclusion_number;

  return inclusionNumber === null || inclusionNumber === undefined
    ? ""
    : String(inclusionNumber);
};

const getSamplePatientCode = (sample = {}) =>
  sample.patientCode ||
  sample.patientKey ||
  sample.patient?.patientCode ||
  sample.patient?.patient_code ||
  sample.patient?.code ||
  sample.patient?.number ||
  "";

const getSampleProductionId = (sample = {}) =>
  sample.productionId ??
  sample.production_id ??
  sample.production?.id ??
  sample.Production?.id ??
  "";

const getSampleCenterId = (sample = {}) =>
  sample.centerId ||
  sample.center_id ||
  sample.center?.id ||
  sample.Center?.id ||
  sample.patient?.centerId ||
  sample.patient?.center_id ||
  "";

const getSampleVisitIds = (sample = {}) =>
  (sample.visits || [])
    .map((visit) => Number(visit.id))
    .filter((visitId) => Number.isInteger(visitId) && visitId > 0);

const getSampleTypeName = (sample = {}) =>
  sample.sampleTypeDefinition?.name || sample.type || "";

const getActionResultData = (response) => response?.data || response;

const normalizeIdValue = (value) => {
  const normalizedValue = Number(value);
  return Number.isInteger(normalizedValue) && normalizedValue > 0
    ? normalizedValue
    : null;
};

const findRecordByName = (records, name) =>
  records.find(
    (record) =>
      String(record.name || "").trim().toLowerCase() ===
      String(name || "").trim().toLowerCase(),
  );

const createInitialFormValues = ({
  patientId = "",
  patientCode = "",
  inclusionNumber = "",
} = {}) => ({
  patientId,
  patientCode,
  inclusionNumber,
  reference: "",
  productionId: OTHER_KIT_VALUE,
  analysisIds: [],
  sampleType: "",
  sampleTypeId: "",
  visitIds: [],
  quantity: "",
  status: "center",
});

const buildValuesFromSample = (sample) => ({
  patientId: sample.patientId ?? sample.patient?.id ?? "",
  patientCode: getSamplePatientCode(sample),
  inclusionNumber: getPatientInclusionNumber(sample.patient),
  reference: sample.reference || "",
  productionId: getSampleProductionId(sample) || OTHER_KIT_VALUE,
  analysisIds: (sample.analyses || []).map((analysis) =>
    Number(analysis.id),
  ),
  sampleType: normalizeSampleType(getSampleTypeName(sample)),
  sampleTypeId:
    sample.sampleTypeId ?? sample.sampleTypeDefinition?.id ?? "",
  visitIds: getSampleVisitIds(sample),
  quantity: sample.quantity ?? "",
  status: sample.status || "center",
});

export const SampleFormContent = ({
  sampleId,
  initialSample,
  studyId,
  centerId,
  onCancel,
  onRemove,
  onSaved,
  onDeleted,
  validateBeforeSave,
  showDelete = false,
  formId = "sample-form",
  resetKey,
  initialPatientId = "",
  initialPatientCode = "",
  initialInclusionNumber = "",
  layout = "vertical",
  backgroundColor = "background.paper",
  sampleTypeFieldLabel,
  autoSelectSingleVisit = false,
  singleAnalysisSelect = false,
}) => {
  const { t } = useTranslation();
  const resolvedAnalysisFieldLabel = singleAnalysisSelect
    ? t("analysis", "Analysis")
    : t("analyses", "Analyses");
  const resolvedSampleTypeFieldLabel =
    sampleTypeFieldLabel || t("type", "Type");
  const isEditMode = Boolean(sampleId && sampleId !== "new");
  const sample = useSelector((state) => state.sample.sample);
  const analyses = useSelector((state) => state.analysis.analyses) || [];
  const productions = useSelector((state) => state.production.productions) || [];
  const patients = useSelector((state) => state.patient?.patients) || [];
  const [values, setValues] = React.useState(createInitialFormValues);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [relationSelectionSource, setRelationSelectionSource] =
    React.useState(null);
  const [visitOptions, setVisitOptions] = React.useState([]);
  const loadedSample =
    isEditMode &&
    initialSample &&
    String(initialSample.id) === String(sampleId)
      ? initialSample
      : isEditMode && sample && String(sample.id) === String(sampleId)
        ? sample
        : null;
  const effectiveCenterId = centerId || getSampleCenterId(loadedSample);

  const setField = React.useCallback((field, value) => {
    setValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));
  }, []);

  React.useEffect(() => {
    if (studyId) {
      store.dispatch(getAnalysesByStudy(studyId));
    }
  }, [studyId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadSampleSetup = async () => {
      if (!studyId) {
        setVisitOptions([]);
        return;
      }

      try {
        const studyVisits = await getSampleSetupItems("visits", studyId);

        if (isMounted) {
          setVisitOptions(
            [...(studyVisits || [])].sort((left, right) =>
              String(left.name || "").localeCompare(String(right.name || "")),
            ),
          );
        }
      } catch (error) {
        console.error("Failed to load study sample setup:", error);
        if (isMounted) {
          setVisitOptions([]);
        }
      }
    };

    loadSampleSetup();

    return () => {
      isMounted = false;
    };
  }, [studyId]);

  React.useEffect(() => {
    if (effectiveCenterId) {
      store.dispatch(getProductionsByCenter(effectiveCenterId));
      store.dispatch(getPatientsByCenter(effectiveCenterId));
    }
  }, [effectiveCenterId]);

  React.useEffect(() => {
    if (isEditMode && !initialSample) {
      store.dispatch(getSample(sampleId));
    }
  }, [initialSample, isEditMode, sampleId]);

  React.useEffect(() => {
    if (!isEditMode) {
      setValues(
        createInitialFormValues({
          patientId: initialPatientId,
          patientCode: initialPatientCode,
          inclusionNumber: initialInclusionNumber,
        }),
      );
      setRelationSelectionSource(null);
      setErrorMessage("");
    }
  }, [
    initialInclusionNumber,
    initialPatientCode,
    initialPatientId,
    isEditMode,
    resetKey,
  ]);

  React.useEffect(() => {
    if (loadedSample) {
      const nextValues = buildValuesFromSample(loadedSample);

      if (singleAnalysisSelect) {
        nextValues.analysisIds = nextValues.analysisIds.slice(0, 1);
      }

      setValues(nextValues);
      setRelationSelectionSource(
        nextValues.analysisIds.length > 0
          ? "analyses"
          : nextValues.visitIds.length > 0
            ? "visits"
            : null,
      );
      setErrorMessage("");
    }
  }, [loadedSample, singleAnalysisSelect]);

  const kitProductions = React.useMemo(() => {
    const optionsById = new Map();

    productions
      .filter(
        (production) =>
          normalizeProductionStatus(production.status) === "at center" &&
          getProductionMaterialType(production) === "material" &&
          (!effectiveCenterId ||
            String(production.centerId) === String(effectiveCenterId)),
      )
      .forEach((production) => {
        optionsById.set(Number(production.id), production);
      });

    const selectedProduction =
      loadedSample?.production || loadedSample?.Production;
    if (
      selectedProduction?.id &&
      getProductionMaterialType(selectedProduction) === "material"
    ) {
      optionsById.set(Number(selectedProduction.id), selectedProduction);
    }

    return Array.from(optionsById.values());
  }, [effectiveCenterId, loadedSample, productions]);

  const selectedProduction = React.useMemo(
    () =>
      kitProductions.find(
        (production) =>
          String(production.id) ===
          String(normalizeKitValue(values.productionId)),
      ) || null,
    [kitProductions, values.productionId],
  );

  const selectedProductionMaterialId = selectedProduction
    ? getProductionMaterialId(selectedProduction)
    : null;
  const selectedMaterialId =
    selectedProductionMaterialId === null ||
    selectedProductionMaterialId === undefined
      ? null
      : Number(selectedProductionMaterialId);

  const analysesByColorAndName = React.useMemo(
    () => [...analyses].sort(compareAnalysesByColorAndName),
    [analyses],
  );

  const relatedAnalysisIds = React.useMemo(
    () => getRelatedAnalysisIds(analyses, values.visitIds),
    [analyses, values.visitIds],
  );

  const availableAnalyses = React.useMemo(() => {
    const selectedIds = new Set(
      values.analysisIds.map((analysisId) => Number(analysisId)),
    );
    const optionsById = new Map();
    const shouldShowAllAnalyses =
      !normalizeKitValue(values.productionId) ||
      values.productionId === OTHER_KIT_VALUE;

    analysesByColorAndName.forEach((analysis) => {
      const analysisId = Number(analysis.id);
      const isSelected = selectedIds.has(analysisId);
      const isAvailableForKit =
        shouldShowAllAnalyses ||
        (Number.isInteger(selectedMaterialId) &&
          getAnalysisMaterialIds(analysis).includes(selectedMaterialId));
      const isAvailableForVisits =
        relationSelectionSource !== "visits" ||
        values.visitIds.length === 0 ||
        relatedAnalysisIds.has(analysisId);

      if ((isAvailableForKit || isSelected) && isAvailableForVisits) {
        optionsById.set(analysisId, analysis);
      }
    });

    return Array.from(optionsById.values());
  }, [
    analysesByColorAndName,
    relatedAnalysisIds,
    relationSelectionSource,
    selectedMaterialId,
    values.analysisIds,
    values.productionId,
    values.visitIds.length,
  ]);

  const relatedVisitIds = React.useMemo(
    () => getRelatedVisitIds(analyses, values.analysisIds),
    [analyses, values.analysisIds],
  );

  const selectedAnalyses = React.useMemo(() => {
    const analysesById = new Map(
      analyses.map((analysis) => [Number(analysis.id), analysis]),
    );

    return values.analysisIds
      .map((analysisId) => analysesById.get(Number(analysisId)))
      .filter(Boolean);
  }, [analyses, values.analysisIds]);

  const selectedAnalysisContainers = React.useMemo(() => {
    const containersByKey = new Map();

    selectedAnalyses.forEach((analysis) => {
      const container = getAnalysisContainer(analysis);

      if (container) {
        containersByKey.set(getAnalysisContainerKey(container), container);
      }
    });

    return Array.from(containersByKey.values());
  }, [selectedAnalyses]);

  const availableSampleTypes = React.useMemo(() => {
    const sampleTypesById = new Map();

    selectedAnalyses.flatMap(getAnalysisSampleTypes).forEach((sampleType) => {
      if (sampleType?.id) {
        sampleTypesById.set(Number(sampleType.id), sampleType);
      }
    });

    return Array.from(sampleTypesById.values()).sort((left, right) =>
      String(left.name || "").localeCompare(String(right.name || "")),
    );
  }, [selectedAnalyses]);

  React.useEffect(() => {
    if (selectedAnalyses.length !== values.analysisIds.length) {
      return;
    }

    const currentSampleType =
      availableSampleTypes.find(
        (sampleType) => String(sampleType.id) === String(values.sampleTypeId),
      ) || findRecordByName(availableSampleTypes, values.sampleType);
    const nextSampleType = currentSampleType || availableSampleTypes[0];
    const nextSampleTypeId = nextSampleType
      ? String(nextSampleType.id)
      : "";
    const nextSampleTypeName = nextSampleType?.name || "";

    if (
      String(values.sampleTypeId) !== nextSampleTypeId ||
      values.sampleType !== nextSampleTypeName
    ) {
      setValues((currentValues) => ({
        ...currentValues,
        sampleTypeId: nextSampleTypeId,
        sampleType: nextSampleTypeName,
      }));
    }
  }, [
    availableSampleTypes,
    selectedAnalyses.length,
    values.analysisIds.length,
    values.sampleTypeId,
    values.sampleType,
  ]);

  const availableVisitOptions = React.useMemo(() => {
    if (
      relationSelectionSource !== "analyses" ||
      values.analysisIds.length === 0
    ) {
      return visitOptions;
    }

    return visitOptions.filter((visit) =>
      relatedVisitIds.has(Number(visit.id)),
    );
  }, [
    relatedVisitIds,
    relationSelectionSource,
    values.analysisIds.length,
    visitOptions,
  ]);

  React.useEffect(() => {
    if (
      relationSelectionSource !== "analyses" ||
      values.visitIds.length === 0 ||
      values.analysisIds.length === 0
    ) {
      return;
    }

    const loadedAnalysisIds = new Set(
      analyses.map((analysis) => Number(analysis.id)),
    );
    const allSelectedAnalysesLoaded = values.analysisIds.every((analysisId) =>
      loadedAnalysisIds.has(Number(analysisId)),
    );

    if (allSelectedAnalysesLoaded) {
      const validVisitIds = values.visitIds.filter((visitId) =>
        relatedVisitIds.has(Number(visitId)),
      );

      if (validVisitIds.length !== values.visitIds.length) {
        setField("visitIds", validVisitIds);
      }
    }
  }, [
    analyses,
    relatedVisitIds,
    relationSelectionSource,
    setField,
    values.analysisIds,
    values.visitIds,
  ]);

  const patientOptions = React.useMemo(() => {
    const optionsById = new Map();

    patients.forEach((patient) => {
      const code = getPatientCode(patient);
      if (patient.id || code) {
        optionsById.set(patient.id || code, patient);
      }
    });

    if (loadedSample?.patient) {
      const patient = loadedSample.patient;
      optionsById.set(patient.id || getPatientCode(patient), patient);
    }

    return Array.from(optionsById.values()).sort(
      (firstPatient, secondPatient) =>
        getPatientCode(firstPatient).localeCompare(
          getPatientCode(secondPatient),
        ),
    );
  }, [loadedSample, patients]);

  const patientCodeOptions = React.useMemo(
    () => [
      ...new Set(
        patientOptions.map((patient) => getPatientCode(patient)).filter(Boolean),
      ),
    ],
    [patientOptions],
  );

  const handlePatientCodeChange = React.useCallback(
    (nextInputValue) => {
      const matchingPatient = patientOptions.find(
        (patient) => getPatientCode(patient) === nextInputValue.trim(),
      );

      setValues((currentValues) => ({
        ...currentValues,
        patientId: matchingPatient?.id || "",
        patientCode: nextInputValue,
        inclusionNumber: matchingPatient
          ? getPatientInclusionNumber(matchingPatient)
          : currentValues.inclusionNumber,
      }));
    },
    [patientOptions],
  );

  React.useEffect(() => {
    if (isEditMode || !values.patientCode.trim()) {
      return;
    }

    const matchingPatient = patientOptions.find(
      (patient) => getPatientCode(patient) === values.patientCode.trim(),
    );

    if (
      matchingPatient?.id &&
      String(values.patientId) !== String(matchingPatient.id)
    ) {
      setValues((currentValues) => ({
        ...currentValues,
        patientId: matchingPatient.id,
        inclusionNumber: getPatientInclusionNumber(matchingPatient),
      }));
    }
  }, [
    isEditMode,
    patientOptions,
    values.patientCode,
    values.patientId,
  ]);

  const handleAnalysisChange = (event) => {
    const nextValue = event.target.value;
    const nextAnalysisIds = singleAnalysisSelect
      ? nextValue === ""
        ? []
        : [Number(nextValue)]
      : (typeof nextValue === "string" ? nextValue.split(",") : nextValue).map(
          (value) => Number(value),
        );
    const nextRelatedVisitIds = getRelatedVisitIds(
      analyses,
      nextAnalysisIds,
    );
    const visitsDriveSelection =
      relationSelectionSource === "visits" && values.visitIds.length > 0;

    setValues((currentValues) => ({
      ...currentValues,
      analysisIds: nextAnalysisIds,
      visitIds: getVisitIdsAfterAnalysisChange({
        autoSelectSingleVisit,
        currentVisitIds: currentValues.visitIds,
        nextAnalysisIds,
        nextRelatedVisitIds,
        visitOptions,
        visitsDriveSelection,
      }),
    }));
    setRelationSelectionSource(
      nextAnalysisIds.length === 0
        ? values.visitIds.length > 0
          ? "visits"
          : null
        : visitsDriveSelection
          ? "visits"
          : "analyses",
    );
  };

  const renderSelectedAnalyses = (selected) => {
    const selectedValues = singleAnalysisSelect ? [selected] : selected;
    const selectedIds = new Set(
      selectedValues
        .filter((analysisId) => analysisId !== "")
        .map((analysisId) => Number(analysisId)),
    );

    return renderAnalysisNames(
      analysesByColorAndName.filter((analysis) =>
        selectedIds.has(Number(analysis.id)),
      ),
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const useInlineFeedback = layout === "horizontal";
    const normalizedQuantity = Number(values.quantity);

    if (
      !values.patientCode.trim() ||
      values.analysisIds.length === 0 ||
      values.visitIds.length === 0 ||
      values.quantity === "" ||
      !Number.isSafeInteger(normalizedQuantity) ||
      normalizedQuantity <= 0
    ) {
      setErrorMessage(
        t("pleaseFillAllRequiredFields", "Please fill all required fields."),
      );
      return;
    }

    const normalizedCenterId = effectiveCenterId
      ? Number(effectiveCenterId)
      : null;
    const normalizedProductionId = normalizeKitValue(values.productionId)
      ? Number(values.productionId)
      : null;
    const payload = {
      patientCode: String(values.patientCode || "").trim(),
      patientId: values.patientId || null,
      inclusionNumber: String(values.inclusionNumber || "").trim() || null,
      reference:
        selectedProduction?.reference || values.reference.trim() || null,
      centerId: normalizedCenterId,
      productionId: normalizedProductionId,
      analysisIds: singleAnalysisSelect
        ? values.analysisIds.slice(0, 1)
        : values.analysisIds,
      type: normalizeSampleTypeForSubmit(values.sampleType),
      visitIds: values.visitIds,
      sampleTypeId: normalizeIdValue(values.sampleTypeId),
      quantity: normalizedQuantity,
      status: isEditMode ? values.status : "center",
    };

    const validationError = validateBeforeSave?.(payload);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      if (!useInlineFeedback) {
        showLoadingAlert(
          isEditMode
            ? t("updatingSample", "Updating sample...")
            : t("creatingSample", "Creating sample..."),
        );
      }

      const response = isEditMode
        ? await store.dispatch(editSample(sampleId, payload))
        : await store.dispatch(createSample(payload));
      const savedSample = getActionResultData(response);

      if (!useInlineFeedback) {
        closeLoadingAlert();
      }
      await onSaved?.(savedSample);
    } catch (error) {
      if (!useInlineFeedback) {
        closeLoadingAlert();
      }
      console.error("Failed to save sample:", error);
      setErrorMessage(
        isEditMode
          ? t("failedToUpdateSample", "Failed to update sample")
          : t("failedToCreateSample", "Failed to create sample"),
      );
      if (!useInlineFeedback) {
        showErrorAlert(
          isEditMode
            ? t("failedToUpdateSample", "Failed to update sample")
            : t("failedToCreateSample", "Failed to create sample"),
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode) {
      return;
    }

    const result = await Swal.fire({
      text: t(
        "confirmDeleteSample",
        "Are you sure you want to delete this sample?",
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("delete", "Delete"),
      cancelButtonText: t("cancel", "Cancel"),
      confirmButtonColor: "#d32f2f",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      showLoadingAlert(t("deletingSample", "Deleting sample..."));
      await store.dispatch(deleteSample(sampleId));
      closeLoadingAlert();
      await onDeleted?.(sampleId);
    } catch (error) {
      closeLoadingAlert();
      console.error("Failed to delete sample:", error);
      setErrorMessage(t("failedToDeleteSample", "Failed to delete sample"));
      showErrorAlert(t("failedToDeleteSample", "Failed to delete sample"));
    } finally {
      setIsSaving(false);
    }
  };

  if (layout === "horizontal") {
    const selectedAnalysisForQuantity =
      selectedAnalyses.length === 1 ? selectedAnalyses[0] : null;
    const defaultTotalQuantity = getAnalysisVisitsTotalQuantity(
      selectedAnalysisForQuantity,
      values.visitIds,
      "",
    );

    return (
      <Box
        component="form"
        id={formId}
        onSubmit={handleSubmit}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(170px, 1.55fr) minmax(130px, 0.9fr) minmax(210px, 1.45fr) minmax(150px, 1.25fr) minmax(150px, 0.85fr) auto",
          },
          alignItems: "start",
          gap: 1,
          px: 1.5,
          py: 1.25,
          bgcolor: backgroundColor,
        }}>
        <FormControl fullWidth required size="small">
          <InputLabel id={`${formId}-inline-analyses-label`}>
            {resolvedAnalysisFieldLabel}
          </InputLabel>
          <Select
            labelId={`${formId}-inline-analyses-label`}
            multiple={!singleAnalysisSelect}
            value={
              singleAnalysisSelect
                ? (values.analysisIds[0] ?? "")
                : values.analysisIds
            }
            sx={values.analysisIds.length > 0 ? clearableSelectSx : undefined}
            onChange={handleAnalysisChange}
            input={<OutlinedInput label={resolvedAnalysisFieldLabel} />}
            renderValue={renderSelectedAnalyses}>
            {availableAnalyses.length === 0 ? (
              <MenuItem disabled>
                {t("noAnalysesAvailable", "No analyses available")}
              </MenuItem>
            ) : (
              availableAnalyses.map((analysis) => (
                <MenuItem key={analysis.id} value={analysis.id}>
                  {singleAnalysisSelect ? (
                    <AnalysisName analysis={analysis} />
                  ) : (
                    <>
                      <Checkbox
                        checked={values.analysisIds.includes(
                          Number(analysis.id),
                        )}
                      />
                      <ListItemText
                        primary={<AnalysisName analysis={analysis} />}
                      />
                    </>
                  )}
                </MenuItem>
              ))
            )}
          </Select>
          {values.analysisIds.length > 0 && (
            <IconButton
              type="button"
              size="small"
              aria-label={t("clearAnalyses", "Clear analyses")}
              title={t("clearAnalyses", "Clear analyses")}
              onMouseDown={stopSelectFromOpening}
              onClick={(event) => {
                event.stopPropagation();
                setField("analysisIds", []);
                setRelationSelectionSource(
                  values.visitIds.length > 0 ? "visits" : null,
                );
              }}
              sx={clearButtonSx}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id={`${formId}-inline-type-label`} shrink>
            {resolvedSampleTypeFieldLabel}
          </InputLabel>
          <Select
            labelId={`${formId}-inline-type-label`}
            value={values.sampleTypeId}
            label={resolvedSampleTypeFieldLabel}
            displayEmpty
            renderValue={(selectedSampleTypeId) => {
              if (selectedSampleTypeId === "") {
                return t("default", "Default");
              }

              return (
                availableSampleTypes.find(
                  (sampleType) =>
                    String(sampleType.id) === String(selectedSampleTypeId),
                )?.name || ""
              );
            }}
            onChange={(event) => {
              const nextSampleTypeId = event.target.value;
              const nextSampleType = availableSampleTypes.find(
                (sampleType) =>
                  String(sampleType.id) === String(nextSampleTypeId),
              );

              setValues((currentValues) => ({
                ...currentValues,
                sampleTypeId: nextSampleTypeId,
                sampleType: nextSampleType?.name || "",
              }));
            }}>
            {availableSampleTypes.length === 0 && (
              <MenuItem value="">
                <ListItemText
                  primary={t("default", "Default")}
                  secondary={
                    defaultTotalQuantity === null
                      ? null
                      : `${t("totalQuantity", "Total quantity")}: ${defaultTotalQuantity}`
                  }
                  secondaryTypographyProps={{
                    fontStyle: "italic",
                    fontSize: "0.7rem",
                    lineHeight: 1.1,
                  }}
                />
              </MenuItem>
            )}
            {availableSampleTypes.map((sampleType) => {
              const totalQuantity = getAnalysisVisitsTotalQuantity(
                selectedAnalysisForQuantity,
                values.visitIds,
                sampleType.id,
              );

              return (
                <MenuItem
                  key={sampleType.id}
                  value={String(sampleType.id)}>
                  <ListItemText
                    primary={sampleType.name}
                    secondary={
                      totalQuantity === null
                        ? null
                        : `${t("totalQuantity", "Total quantity")}: ${totalQuantity}`
                    }
                    secondaryTypographyProps={{
                      fontStyle: "italic",
                      fontSize: "0.7rem",
                      lineHeight: 1.1,
                    }}
                  />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <FormControl fullWidth required size="small">
          <InputLabel id={`${formId}-inline-visits-label`}>
            {t("visits", "Visits")}
          </InputLabel>
          <Select
            labelId={`${formId}-inline-visits-label`}
            multiple
            value={values.visitIds}
            sx={values.visitIds.length > 0 ? clearableSelectSx : undefined}
            onChange={(event) => {
              const nextValue = event.target.value;
              const nextVisitIds = (typeof nextValue === "string"
                ? nextValue.split(",")
                : nextValue
              ).map((value) => Number(value));
              const nextRelatedAnalysisIds = getRelatedAnalysisIds(
                analyses,
                nextVisitIds,
              );
              const analysesDriveSelection =
                relationSelectionSource === "analyses" &&
                values.analysisIds.length > 0;

              setValues((currentValues) => ({
                ...currentValues,
                visitIds: nextVisitIds,
                analysisIds:
                  analysesDriveSelection || nextVisitIds.length === 0
                    ? currentValues.analysisIds
                    : currentValues.analysisIds.filter((analysisId) =>
                        nextRelatedAnalysisIds.has(Number(analysisId)),
                      ),
              }));
              setRelationSelectionSource(
                nextVisitIds.length === 0
                  ? values.analysisIds.length > 0
                    ? "analyses"
                    : null
                  : analysesDriveSelection
                    ? "analyses"
                    : "visits",
              );
            }}
            input={<OutlinedInput label={t("visits", "Visits")} />}
            renderValue={(selected) => {
              const selectedIds = new Set(
                selected.map((visitId) => Number(visitId)),
              );

              return availableVisitOptions
                .filter((visit) => selectedIds.has(Number(visit.id)))
                .map((visit) => visit.name)
                .join(", ");
            }}>
            {availableVisitOptions.length === 0 ? (
              <MenuItem disabled>
                {t("noVisitsAvailable", "No visits available")}
              </MenuItem>
            ) : (
              availableVisitOptions.map((visit) => {
                const visitQuantity = getAnalysisVisitQuantity(
                  selectedAnalysisForQuantity,
                  visit.id,
                  values.sampleTypeId,
                );

                return (
                  <MenuItem key={visit.id} value={Number(visit.id)}>
                    <Checkbox
                      checked={values.visitIds.includes(Number(visit.id))}
                    />
                    <ListItemText
                      primary={visit.name}
                      secondary={
                        visitQuantity === null
                          ? null
                          : `${t("quantity", "Quantity")}: ${visitQuantity}`
                      }
                      secondaryTypographyProps={{
                        fontStyle: "italic",
                        fontSize: "0.7rem",
                        lineHeight: 1.1,
                      }}
                    />
                  </MenuItem>
                );
              })
            )}
          </Select>
          {values.visitIds.length > 0 && (
            <IconButton
              type="button"
              size="small"
              aria-label={t("clearVisits", "Clear visits")}
              title={t("clearVisits", "Clear visits")}
              onMouseDown={stopSelectFromOpening}
              onClick={(event) => {
                event.stopPropagation();
                setField("visitIds", []);
                setRelationSelectionSource(
                  values.analysisIds.length > 0 ? "analyses" : null,
                );
              }}
              sx={clearButtonSx}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel id={`${formId}-inline-container-label`}>
            {t("analysisContainer", "Container")}
          </InputLabel>
          <Select
            multiple
            labelId={`${formId}-inline-container-label`}
            value={selectedAnalysisContainers.map(getAnalysisContainerKey)}
            label={t("analysisContainer", "Container")}
            disabled
            renderValue={() =>
              renderAnalysisContainerList(selectedAnalysisContainers)
            }>
            {selectedAnalysisContainers.map((container) => (
              <MenuItem
                key={getAnalysisContainerKey(container)}
                value={getAnalysisContainerKey(container)}>
                {renderAnalysisContainerLabel(container)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          required
          size="small"
          type="number"
          label={t("totalNumberOfSample", "Total number of sample")}
          value={values.quantity}
          onChange={(event) => setField("quantity", event.target.value)}
          inputProps={{ min: 1, step: 1 }}
        />

        {isEditMode ? (
          <Box
            sx={{
              minHeight: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
            }}>
            <IconButton
              type="submit"
              size="medium"
              color="primary"
              disabled={isSaving}
              aria-label={t("update", "Update")}
              title={t("update", "Update")}
              sx={{ width: 36, height: 36 }}>
              <CheckIcon fontSize="medium" />
            </IconButton>
            <IconButton
              type="button"
              size="medium"
              color="secondary"
              disabled={isSaving}
              aria-label={t("cancel", "Cancel")}
              title={t("cancel", "Cancel")}
              sx={{ width: 36, height: 36 }}
              onClick={onRemove || onCancel}>
              <CloseIcon fontSize="medium" />
            </IconButton>
          </Box>
        ) : (
          <Box
            sx={{
              minHeight: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={isSaving}
              startIcon={<AddIcon fontSize="small" />}
              sx={{ minHeight: 36, px: 2 }}>
              {t("add", "Add")}
            </Button>
          </Box>
        )}

        {errorMessage && (
          <Box
            sx={{
              gridColumn: "1 / -1",
              color: "#721c24",
              backgroundColor: "#f8d7da",
              borderColor: "#f5c6cb",
              p: 1,
              border: "1px solid transparent",
              borderRadius: 1,
            }}>
            {errorMessage}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box component="form" id={formId} onSubmit={handleSubmit}>
      <Grid container spacing={2} sx={{ pt: 1 }}>
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Autocomplete
            freeSolo
            openOnFocus
            autoHighlight
            clearOnBlur={false}
            selectOnFocus
            disabled={!effectiveCenterId}
            options={patientCodeOptions}
            value={values.patientCode || null}
            inputValue={values.patientCode}
            getOptionLabel={(option) => option || ""}
            isOptionEqualToValue={(option, value) => option === value}
            onChange={(event, nextValue) => {
              const nextCode = nextValue || "";
              const matchingPatient = patientOptions.find(
                (patient) => getPatientCode(patient) === nextCode.trim(),
              );

              setValues((currentValues) => ({
                ...currentValues,
                patientId: matchingPatient?.id || "",
                patientCode: nextCode,
                inclusionNumber: matchingPatient
                  ? getPatientInclusionNumber(matchingPatient)
                  : currentValues.inclusionNumber,
              }));
            }}
            onInputChange={(event, nextInputValue) =>
              handlePatientCodeChange(nextInputValue)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                required
                label={t("patientCode", "Patient Code")}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("inclusionNumber", "Inclusion Number")}
            value={values.inclusionNumber}
            onChange={(event) =>
              setField("inclusionNumber", event.target.value)
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        {/* <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id={`${formId}-kit-label`}>
              {t("kit", "Kit")}
            </InputLabel>
            <Select
              labelId={`${formId}-kit-label`}
              value={values.productionId}
              label={t("kit", "Kit")}
              onChange={(event) => {
                setValues((currentValues) => ({
                  ...currentValues,
                  productionId: event.target.value,
                  analysisIds: [],
                }));
              }}
              renderValue={(selected) => {
                if (selected === OTHER_KIT_VALUE) {
                  return t("other", "Other");
                }

                const production = kitProductions.find(
                  (currentProduction) =>
                    String(currentProduction.id) === String(selected),
                );
                return production ? getProductionLabel(production) : "";
              }}>
              <MenuItem value="" disabled>
                <em>{t("selectKit", "Select kit")}</em>
              </MenuItem>
              <MenuItem value={OTHER_KIT_VALUE}>
                {t("other", "Other")}
              </MenuItem>
              {kitProductions.map((production) => (
                <MenuItem key={production.id} value={production.id}>
                  {getProductionLabel(production) ||
                    `${t("kit", "Kit")} ${production.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid> */}
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel id={`${formId}-analyses-label`}>
              {resolvedAnalysisFieldLabel}
            </InputLabel>
            <Select
              labelId={`${formId}-analyses-label`}
              multiple={!singleAnalysisSelect}
              value={
                singleAnalysisSelect
                  ? (values.analysisIds[0] ?? "")
                  : values.analysisIds
              }
              sx={
                values.analysisIds.length > 0 ? clearableSelectSx : undefined
              }
              onChange={handleAnalysisChange}
              input={<OutlinedInput label={resolvedAnalysisFieldLabel} />}
              renderValue={renderSelectedAnalyses}>
              {availableAnalyses.length === 0 ? (
                <MenuItem disabled>
                  {normalizeKitValue(values.productionId)
                    ? t(
                        "noAnalysesForKit",
                        "No analyses available for this kit",
                      )
                    : t("noAnalysesAvailable", "No analyses available")}
                </MenuItem>
              ) : (
                availableAnalyses.map((analysis) => (
                  <MenuItem key={analysis.id} value={analysis.id}>
                    {singleAnalysisSelect ? (
                      <AnalysisName analysis={analysis} />
                    ) : (
                      <>
                        <Checkbox
                          checked={values.analysisIds.includes(
                            Number(analysis.id),
                          )}
                        />
                        <ListItemText
                          primary={<AnalysisName analysis={analysis} />}
                        />
                      </>
                    )}
                  </MenuItem>
                ))
              )}
            </Select>
            {values.analysisIds.length > 0 && (
              <IconButton
                type="button"
                size="small"
                aria-label={t("clearAnalyses", "Clear analyses")}
                title={t("clearAnalyses", "Clear analyses")}
                onMouseDown={stopSelectFromOpening}
                onClick={(event) => {
                  event.stopPropagation();
                  setField("analysisIds", []);
                  setRelationSelectionSource(
                    values.visitIds.length > 0 ? "visits" : null,
                  );
                }}
                sx={clearButtonSx}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel id={`${formId}-visits-label`}>
              {t("visits", "Visits")}
            </InputLabel>
            <Select
              labelId={`${formId}-visits-label`}
              multiple
              value={values.visitIds}
              sx={values.visitIds.length > 0 ? clearableSelectSx : undefined}
              onChange={(event) => {
                const nextValue = event.target.value;
                const nextVisitIds = (typeof nextValue === "string"
                  ? nextValue.split(",")
                  : nextValue
                ).map((value) => Number(value));
                const nextRelatedAnalysisIds = getRelatedAnalysisIds(
                  analyses,
                  nextVisitIds,
                );
                const analysesDriveSelection =
                  relationSelectionSource === "analyses" &&
                  values.analysisIds.length > 0;

                setValues((currentValues) => ({
                  ...currentValues,
                  visitIds: nextVisitIds,
                  analysisIds:
                    analysesDriveSelection || nextVisitIds.length === 0
                      ? currentValues.analysisIds
                      : currentValues.analysisIds.filter((analysisId) =>
                          nextRelatedAnalysisIds.has(Number(analysisId)),
                        ),
                }));
                setRelationSelectionSource(
                  nextVisitIds.length === 0
                    ? values.analysisIds.length > 0
                      ? "analyses"
                      : null
                    : analysesDriveSelection
                      ? "analyses"
                      : "visits",
                );
              }}
              input={<OutlinedInput label={t("visits", "Visits")} />}
              renderValue={(selected) => {
                const selectedIds = new Set(
                  selected.map((visitId) => Number(visitId)),
                );

                return availableVisitOptions
                  .filter((visit) => selectedIds.has(Number(visit.id)))
                  .map((visit) => visit.name)
                  .join(", ");
              }}>
              {availableVisitOptions.length === 0 ? (
                <MenuItem disabled>
                  {t("noVisitsAvailable", "No visits available")}
                </MenuItem>
              ) : (
                availableVisitOptions.map((visit) => (
                  <MenuItem key={visit.id} value={Number(visit.id)}>
                    <Checkbox
                      checked={values.visitIds.includes(Number(visit.id))}
                    />
                    <ListItemText primary={visit.name} />
                  </MenuItem>
                ))
              )}
            </Select>
            {values.visitIds.length > 0 && (
              <IconButton
                type="button"
                size="small"
                aria-label={t("clearVisits", "Clear visits")}
                title={t("clearVisits", "Clear visits")}
                onMouseDown={stopSelectFromOpening}
                onClick={(event) => {
                  event.stopPropagation();
                  setField("visitIds", []);
                  setRelationSelectionSource(
                    values.analysisIds.length > 0 ? "analyses" : null,
                  );
                }}
                sx={clearButtonSx}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id={`${formId}-analysis-container-label`}>
              {t("analysisContainer", "Container")}
            </InputLabel>
            <Select
              multiple
              labelId={`${formId}-analysis-container-label`}
              value={selectedAnalysisContainers.map(getAnalysisContainerKey)}
              label={t("analysisContainer", "Container")}
              disabled
              renderValue={() =>
                renderAnalysisContainerList(selectedAnalysisContainers)
              }>
              {selectedAnalysisContainers.map((container) => (
                <MenuItem
                  key={getAnalysisContainerKey(container)}
                  value={getAnalysisContainerKey(container)}>
                  {renderAnalysisContainerLabel(container)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id={`${formId}-sample-type-label`}>
              {resolvedSampleTypeFieldLabel}
            </InputLabel>
            <Select
              labelId={`${formId}-sample-type-label`}
              value={values.sampleTypeId}
              label={resolvedSampleTypeFieldLabel}
              onChange={(event) => {
                const nextSampleTypeId = event.target.value;
                const nextSampleType = availableSampleTypes.find(
                  (sampleType) =>
                    String(sampleType.id) === String(nextSampleTypeId),
                );

                setValues((currentValues) => ({
                  ...currentValues,
                  sampleTypeId: nextSampleTypeId,
                  sampleType: nextSampleType?.name || "",
                }));
              }}>
              <MenuItem value="" disabled>
                <em>{t("none", "None")}</em>
              </MenuItem>
              {availableSampleTypes.map((sampleType) => (
                <MenuItem
                  key={sampleType.id}
                  value={String(sampleType.id)}>
                  {sampleType.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            type="number"
            label={t("totalNumberOfSamples", "Total number of samples")}
            value={values.quantity}
            onChange={(event) => setField("quantity", event.target.value)}
            inputProps={{ min: 1, step: 1 }}
          />
        </Grid>
        {/* {isEditMode && (
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel id={`${formId}-status-label`}>
                {t("status", "Status")}
              </InputLabel>
              <Select
                labelId={`${formId}-status-label`}
                value={values.status}
                label={t("status", "Status")}
                onChange={(event) => setField("status", event.target.value)}>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="shipping">Shipping</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )} */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            }}>
            <Box>
              {showDelete && isEditMode && (
                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  disabled={isSaving}
                  onClick={handleDelete}>
                  {t("delete", "Delete")}
                </Button>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button variant="text" onClick={onCancel} disabled={isSaving}>
                {t("cancel", "Cancel")}
              </Button>
              <Button type="submit" variant="contained" disabled={isSaving}>
                {isEditMode ? t("update", "Update") : t("create", "Create")}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {errorMessage && (
        <Box
          sx={{
            mt: 2,
            color: "#721c24",
            backgroundColor: "#f8d7da",
            borderColor: "#f5c6cb",
            p: 1.5,
            border: "1px solid transparent",
            borderRadius: 1,
          }}>
          {errorMessage}
        </Box>
      )}
    </Box>
  );
};

export const ScreenSampleForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const sampleId = params.id;
  const isEditMode = Boolean(sampleId && sampleId !== "new");
  const routeState = React.useMemo(
    () => ({
      studyId: location.state?.studyId,
      studyCode: location.state?.studyCode,
      centerNumber: location.state?.centerNumber,
      centerId: location.state?.centerId,
    }),
    [
      location.state?.centerId,
      location.state?.centerNumber,
      location.state?.studyCode,
      location.state?.studyId,
    ],
  );

  React.useEffect(() => {
    if (!routeState.studyId && !isEditMode) {
      navigate("/samples", { replace: true, state: routeState });
    }
  }, [isEditMode, navigate, routeState]);

  const handleDone = React.useCallback(() => {
    navigate("/samples", { replace: true, state: routeState });
  }, [navigate, routeState]);

  return (
    <Grid container justifyContent="center" sx={{ pb: 4 }}>
      <Grid item xs={12} md={8} lg={6}>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {isEditMode
                ? t("editSample", "Edit sample")
                : t("createSample", "Create sample")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                "pleaseIndicateFields",
                "Please indicate the following fields:",
              )}
            </Typography>
          </Box>

          <SampleFormContent
            sampleId={sampleId}
            studyId={routeState.studyId}
            centerId={routeState.centerId}
            onCancel={() => navigate("/samples", { state: routeState })}
            onSaved={handleDone}
            onDeleted={handleDone}
            showDelete
            formId="sample-page-form"
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ScreenSampleForm;
